/**
 * خريطة Breadcrumbs المركزيّة لكامل الموقع.
 * مفتاح = pathname (بدون $params) → عنوان ar/en + parent اختياريّ.
 * المسارات الديناميكيّة ($handle/$slug/$id…) تُحلّ في SiteBreadcrumbs.tsx.
 *
 * 🔗 صفحات /admin/* تُولَّد آلياً من ADMIN_ACTIONS، لكنّ التوليد يعيش في
 *    breadcrumbsMap.admin.ts (يُحمَّل مع كود الأدمن فقط) لأنّ سحب الـregistry
 *    إلى هنا كان يضخّ ~٣٥ ك.ب من كود الأدمن في ملفّ إقلاع الزائر.
 *    المدخلات اليدويّة أدناه تغطّي كلّ المسارات وتتقدّم على التوليد.
 */

export type Crumb = {
  ar: string;
  en: string;
  /** المسار الأب — null أو غياب = العنصر هو جذر سياقه */
  parent?: string | null;
};


/** مدخلات يدويّة: تتجاوز AUTO_ADMIN وتغطّي ما ليس في الـregistry. */
const MANUAL: Record<string, Crumb> = {
  // ===== الجذور =====
  "/": { ar: "الرئيسيّة", en: "Home", parent: null },
  "/account": { ar: "حسابي", en: "My Account", parent: "/" },
  "/admin": { ar: "لوحة التحكّم", en: "Admin", parent: null },
  "/admin/control-center": { ar: "مركز التحكّم", en: "Control Center", parent: "/admin" },

  // ===== صفحات عامّة =====
  "/shop": { ar: "المتجر", en: "Shop", parent: "/" },
  "/about": { ar: "عن الدار", en: "About", parent: "/" },
  "/journal": { ar: "المجلّة", en: "Journal", parent: "/" },
  "/contact": { ar: "تواصل معنا", en: "Contact", parent: "/" },
  "/uae": { ar: "الإمارات", en: "UAE", parent: "/" },

  "/gifts": { ar: "الإهداء", en: "Gifts", parent: "/" },
  
  "/ambassadors": { ar: "السفراء", en: "Ambassadors", parent: "/" },
  "/ambassadors/terms": { ar: "الشروط والأحكام", en: "Terms & Conditions", parent: "/ambassadors" },
  "/cart": { ar: "السلّة", en: "Cart", parent: "/" },
  "/faq": { ar: "الأسئلة الشائعة", en: "FAQ", parent: "/" },
  "/shipping": { ar: "الشحن", en: "Shipping", parent: "/" },
  "/returns": { ar: "الإرجاع", en: "Returns", parent: "/" },
  "/privacy": { ar: "الخصوصيّة", en: "Privacy", parent: "/" },
  "/terms": { ar: "الشروط", en: "Terms", parent: "/" },
  "/cookies": { ar: "الكوكيز", en: "Cookies", parent: "/" },
  "/accessibility": { ar: "إمكانيّة الوصول", en: "Accessibility", parent: "/" },
  "/copyright": { ar: "حقوق الملكيّة", en: "Copyright", parent: "/" },

  "/track-order": { ar: "تتبّع الطلب", en: "Track Order", parent: "/" },
  
  "/wholesale": { ar: "الجملة", en: "Wholesale", parent: "/" },
  "/auth": { ar: "تسجيل الدخول", en: "Sign In", parent: "/" },
  "/forgot-password": { ar: "استرجاع كلمة المرور", en: "Forgot Password", parent: "/auth" },
  "/reset-password": { ar: "إعادة تعيين كلمة المرور", en: "Reset Password", parent: "/auth" },
  "/thank-you": { ar: "شكراً لك", en: "Thank You", parent: "/" },
  "/unsubscribe": { ar: "إلغاء الاشتراك", en: "Unsubscribe", parent: "/" },
  "/body-care-set": { ar: "Body Care Set", en: "Body Care Set", parent: "/" },

  // ===== /account/* =====
  "/account/orders": { ar: "طلباتي", en: "My Orders", parent: "/account" },
  "/account/wishlist": { ar: "المفضّلة", en: "Wishlist", parent: "/account" },
  "/account/profile": { ar: "الملف الشخصيّ", en: "Profile", parent: "/account" },
  "/account/ambassador": { ar: "برنامج السفراء", en: "Ambassador", parent: "/account" },
  
  "/account/coupons": { ar: "بطاقات سحب الرصيد", en: "Balance withdrawal cards", parent: "/account" },
  "/account/ledger": { ar: "سجلّ المحفظة", en: "Ledger", parent: "/account" },
  "/account/recipients": { ar: "المستلمون", en: "Recipients", parent: "/account" },
  "/account/media": { ar: "مكتبة الصور والفيديوهات للنشر", en: "Photo & video sharing library", parent: "/account" },
  "/account/gifts": { ar: "محفظة الهدايا", en: "Gifts Wallet", parent: "/account" },
  "/account/gifts/birthday/$offerId": { ar: "هديّة عيد الميلاد", en: "Birthday Gift", parent: "/account/gifts" },
  "/account/applications": { ar: "طلب الترقية", en: "Upgrade Application", parent: "/account" },
  "/account/analytics": { ar: "إحصاءاتي", en: "My Analytics", parent: "/account" },
  "/account/analytics/select-property": { ar: "اختيار property", en: "Select Property", parent: "/account/analytics" },
  "/account/install-app": { ar: "تثبيت التطبيق", en: "Install the app", parent: "/account/ambassador" },

  // ===== /admin/* =====
  // الجذور الفرعيّة (مجموعات الناف)
  "/admin/ambassadors": { ar: "السفراء", en: "Ambassadors", parent: "/admin" },

  // الطلبات والعملاء
  "/admin/orders": { ar: "الطلبات", en: "Orders", parent: "/admin" },
  "/admin/users": { ar: "العملاء", en: "Users", parent: "/admin" },
  "/admin/demographics": { ar: "الديموغرافيا", en: "Demographics", parent: "/admin" },
  "/admin/pages": { ar: "منشئ الصفحات", en: "Pages Builder", parent: "/admin" },
  "/admin/pages/$id": { ar: "تحرير صفحة", en: "Edit Page", parent: "/admin/pages" },
  "/admin/team": { ar: "الفريق", en: "Team", parent: "/admin" },
  "/admin/payouts": { ar: "السحوبات", en: "Payouts", parent: "/admin" },
  "/admin/drivers": { ar: "السائقون", en: "Drivers", parent: "/admin" },
  "/admin/representatives": { ar: "المندوبون", en: "Representatives", parent: "/admin" },
  "/admin/drivers/$id": { ar: "تقرير السائق", en: "Driver report", parent: "/admin/drivers" },
  "/admin/drivers-monitor": { ar: "مراقبة السائقين", en: "Drivers Monitor", parent: "/admin/drivers" },
  "/admin/alerts": { ar: "التنبيهات", en: "Alerts", parent: "/admin" },
  "/admin/funnels": { ar: "القِمَع", en: "Funnels", parent: "/admin" },
  "/admin/funnel-report": { ar: "تقرير القمعات", en: "Funnel Report", parent: "/admin/funnels" },
  "/admin/wishlist-vs-cart": { ar: "Wishlist مقابل Cart", en: "Wishlist vs Cart", parent: "/admin" },
  "/admin/tracking-health": { ar: "صحّة التتبّع", en: "Tracking Health", parent: "/admin" },
  "/admin/meta-performance": { ar: "أداء ميتا", en: "Meta Performance", parent: "/admin" },
  "/admin/crm": { ar: "إدارة العملاء", en: "CRM", parent: "/admin" },
  "/admin/wholesale": { ar: "طلبات الوكالات", en: "Wholesale Pipeline", parent: "/admin" },


  // مجموعة السفراء
  "/admin/applications": { ar: "طلبات السفراء", en: "Applications", parent: "/admin/ambassadors" },
  "/admin/ambassador-gifts": { ar: "هدايا السفراء", en: "Ambassador Gifts", parent: "/admin/ambassadors" },
  "/admin/preview-as-ambassador": { ar: "معاينة كسفير", en: "Preview as Ambassador", parent: "/admin/ambassadors" },
  "/admin/ambassador-monitor": { ar: "مراقبة سفير", en: "Ambassador Monitor", parent: "/admin/ambassadors" },
  "/admin/milestones-summary": { ar: "ملخّص الإنجازات", en: "Milestones Summary", parent: "/admin/ambassadors" },
  "/admin/celebrities": { ar: "المشاهير", en: "Celebrities", parent: "/admin/ambassadors" },
  "/admin/ambassador-media": { ar: "مكتبة السفراء", en: "Ambassador Media", parent: "/admin/ambassadors" },
  "/admin/ambassador-tree": { ar: "شجرة السفير", en: "Ambassador Tree", parent: "/admin/ambassadors" },
  "/admin/ambassador-sales": { ar: "مبيعات السفراء", en: "Ambassador Sales", parent: "/admin/ambassadors" },
  "/admin/sales-profit": { ar: "أرباح المبيعات", en: "Sales Profit", parent: "/admin" },
  "/admin/media-shares": { ar: "سجلّ مشاركات المكتبة", en: "Media Shares Log", parent: "/admin/ambassador-media" },

  // الهدايا
  "/admin/gifts-hub": { ar: "مركز الهدايا", en: "Gifts Hub", parent: "/admin" },
  "/admin/gifts": { ar: "برامج الهدايا", en: "Gifts", parent: "/admin/gifts-hub" },
  "/admin/birthday-gifts": { ar: "هدايا أعياد الميلاد", en: "Birthday Gifts", parent: "/admin/control-center" },
  "/admin/birthday-gifts/log": { ar: "السجلّ", en: "Log", parent: "/admin/birthday-gifts" },
  "/admin/about-us": { ar: "صفحة من نحن", en: "About Us Page", parent: "/admin" },
  "/admin/gift-flow": { ar: "نصوص نافذة الهديّة", en: "Gift Flow Texts", parent: "/admin" },
  "/admin/phone-messages": { ar: "رسائل الهاتف", en: "Phone Messages", parent: "/admin" },
  "/admin/visitor-dialogs": { ar: "نوافذ الزوّار", en: "Visitor Dialogs", parent: "/admin/control-center" },
  "/admin/visitor-dialogs-analytics": { ar: "إحصاءات نوافذ الزوّار", en: "Visitor Dialogs Analytics", parent: "/admin/visitor-dialogs" },
  "/admin/occasion-reminders": { ar: "تذكيرات المناسبات", en: "Occasion Reminders", parent: "/admin/control-center" },
  "/admin/ambassador-reminders": { ar: "تذكيرات السفراء", en: "Ambassador Reminders", parent: "/admin/control-center" },
  "/admin/ambassador-contact": { ar: "التواصل مع LECMO Lovers", en: "Contact LECMO Lovers", parent: "/admin/control-center" },
  "/admin/gift-pickup": { ar: "استلام هدايا (محلّ)", en: "Gift Pickup", parent: "/admin/control-center" },
  "/admin/gift-accounting": { ar: "محاسبة الهدايا", en: "Gift Accounting", parent: "/admin/control-center" },
  "/admin/pickup-branches": { ar: "فروع الاستلام", en: "Pickup Branches", parent: "/admin" },

  // واجهة الموبايل
  "/admin/highlights": { ar: "بطاقات الموبايل", en: "Highlights", parent: "/admin/control-center" },
  "/admin/shop-collections": { ar: "ترتيب أقسام المتجر", en: "Shop Sections Order", parent: "/admin/control-center" },
  "/admin/page-backgrounds": { ar: "عناصر الصفحات", en: "Page Elements", parent: "/admin/control-center" },
  "/admin/account-menu": { ar: "ترتيب قائمة الحساب", en: "Account Menu Order", parent: "/admin/control-center" },

  "/admin/galleries": { ar: "معارض المجموعات", en: "Galleries", parent: "/admin" },
  "/admin/home-blocks": { ar: "بلوكات الهوم", en: "Home Blocks", parent: "/admin" },
  "/admin/home-info-blocks": { ar: "بلوك المعلومات (ديسكتوب)", en: "Info Block (Desktop)", parent: "/admin" },
  "/admin/home-spotlight": { ar: "البطاقة المميّزة", en: "Spotlight Card", parent: "/admin/control-center" },
  "/admin/featured-products": { ar: "المنتجات المختارة", en: "Featured Products", parent: "/admin" },
  "/admin/discovery-block": { ar: "بلوك Discovery", en: "Discovery Block", parent: "/admin" },
  "/admin/home-editorial-note": { ar: "بلوك ٦", en: "Block 6", parent: "/admin" },
  "/admin/home-desktop": { ar: "هوم الديسكتوب", en: "Home Desktop", parent: "/admin" },
  "/admin/home-texts": { ar: "بلوك ٧ و ٨", en: "Blocks 7 & 8", parent: "/admin" },
  "/admin/home-blocks-hub": { ar: "بلوكات الهوم — مركز واحد", en: "Home blocks hub", parent: "/admin" },
  "/admin/home-block-opacity": { ar: "شفافيّة البلوكات", en: "Block opacity", parent: "/admin" },
  "/admin/newsletter": { ar: "مشتركو النشرة", en: "Newsletter subscribers", parent: "/admin" },

  "/admin/product-translations": { ar: "ترجمات المنتجات", en: "Product Translations", parent: "/admin" },
  "/admin/product-discovery-kit": { ar: "Discovery Kit في صفحة المنتج", en: "Product Discovery Kit", parent: "/admin" },
  "/admin/home-product-video": { ar: "بلوك منتج + فيديو", en: "Home Product + Video", parent: "/admin" },
  "/admin/home-six-grid": { ar: "بلوك ٥", en: "Block 5", parent: "/admin" },
  "/admin/home-quote-video": { ar: "بلوك ٧", en: "Block 7", parent: "/admin" },
  "/admin/new-product": { ar: "صفحة منتج جديد", en: "New Product Page", parent: "/admin" },
  "/new-product": { ar: "الإصدارات الجديدة", en: "New Releases" },
  "/new-product/$slug": { ar: "منتج جديد", en: "New Product", parent: "/new-product" },
  "/admin/home-story-banner": { ar: "بلوك ٨", en: "Block 8", parent: "/admin" },
  "/admin/collection-overlays": { ar: "نص بطاقات المجموعات", en: "Collection Overlays", parent: "/admin/home-blocks" },
  "/admin/home-blocks-simple": { ar: "تحرير مبسَّط للبلوكات", en: "Simple Blocks Editor", parent: "/admin" },
  "/admin/home-about": { ar: "بلوك ٢–٤", en: "Blocks 2–4", parent: "/admin" },
  "/admin/editor-picks": { ar: "نصائح الدار", en: "Editor Picks", parent: "/admin" },
  "/admin/popup-images": { ar: "صور النافذة المنبثقة", en: "Popup Images", parent: "/admin" },
  "/admin/product-lifestyle": { ar: "صور Lifestyle للمنتج", en: "Product Lifestyle", parent: "/admin" },
  "/admin/product-story-blocks": { ar: "بلوكات أسفل المنتج", en: "Product Story Blocks", parent: "/admin" },
  "/admin/intro-splash": { ar: "شاشة الاستهلال", en: "Intro Splash", parent: "/admin" },

  "/admin/thankyou-videos": { ar: "فيديوهات الشكر", en: "Thank-you videos", parent: "/admin" },
  "/admin/product-scent-notes": { ar: "هرم العطر للمنتج", en: "Product Scent Pyramid", parent: "/admin" },
  "/admin/scent-spray": { ar: "بخّة العطر", en: "Scent Spray", parent: "/admin" },
  "/admin/banners": { ar: "البانرات", en: "Banners", parent: "/admin/control-center" },

  // الرسائل
  "/admin/messages": { ar: "رسائل واتساب", en: "WhatsApp Logs", parent: "/admin" },
  "/admin/cart-reminders": { ar: "سجلّ تذكيرات السلّة (للقراءة)", en: "Cart Reminder Log (read-only)", parent: "/admin/control-center" },
  "/admin/marketing-health": { ar: "صحّة التسويق", en: "Marketing Health", parent: "/admin/control-center" },
  "/admin/compliance-composer": { ar: "مولّد ردّ امتثال", en: "Compliance Composer", parent: "/admin/control-center" },
  "/admin/whatsapp-opt-ins": { ar: "موافقات واتساب", en: "WhatsApp Opt-ins", parent: "/admin/control-center" },
  "/admin/whatsapp-templates": { ar: "قوالب واتساب (Meta)", en: "WhatsApp Templates", parent: "/admin/control-center" },
  "/admin/phone-lookup": { ar: "كاشف الرقم", en: "Phone Lookup", parent: "/admin" },
  "/admin/campaign-analytics": { ar: "تحليلات الحملات", en: "Campaign Analytics", parent: "/admin/control-center" },
  "/admin/email-suppressions": { ar: "حظر البريد", en: "Email Suppressions", parent: "/admin" },
  "/admin/storage-cleanup": { ar: "تنظيف المخزن", en: "Storage Cleanup", parent: "/admin" },
  "/admin/pwa-installs": { ar: "تثبيت التطبيق (PWA)", en: "PWA Installs", parent: "/admin" },
  "/admin/auth-funnel": { ar: "قمع تسجيل الدخول", en: "Auth Funnel", parent: "/admin/control-center" },
  "/admin/analytics": { ar: "التحليلات (GA4)", en: "Analytics (GA4)", parent: "/admin" },
  "/admin/ai-credits": { ar: "رصيد AI", en: "AI Credits", parent: "/admin" },
  "/admin/performance": { ar: "أداء الموقع", en: "Site Performance", parent: "/admin" },
  "/admin/campaign-sends": { ar: "سجلّ إرسالات الحملات", en: "Campaign Sends", parent: "/admin/control-center" },
  "/admin/notifications-log": { ar: "إشعارات الطلبات", en: "Order Notifications", parent: "/admin" },
  "/admin/web-push": { ar: "إشعارات المتصفّح", en: "Web Push", parent: "/admin" },
  "/admin/reminder-campaigns": { ar: "حملات التذكير", en: "Reminder Campaigns", parent: "/admin/control-center" },
  "/admin/seasonal-campaigns": { ar: "حملات موسميّة", en: "Seasonal Campaigns", parent: "/admin/control-center" },
  "/admin/campaigns": { ar: "حملات التواصل الجماعيّة", en: "Broadcast Campaigns", parent: "/admin/control-center" },
  "/admin/exit-intent": { ar: "نافذة المغادرة", en: "Exit Intent", parent: "/admin/control-center" },

  // الوكيل
  "/admin/agent": { ar: "إعدادات الوكيل", en: "Agent Settings", parent: "/admin/control-center" },
  "/admin/agent-rules": { ar: "قواعد الوكيل", en: "Agent Rules", parent: "/admin/agent" },
  "/admin/agent-chats": { ar: "محادثات الوكيل", en: "Agent Chats", parent: "/admin/agent" },
  "/admin/agent-insights": { ar: "تحليلات الوكيل", en: "Agent Insights", parent: "/admin/agent" },
  "/admin/agent-feedback": { ar: "ملاحظات الوكيل", en: "Agent Feedback", parent: "/admin/agent" },

  // الإعدادات والفحص
  "/admin/business-settings": { ar: "إعدادات تجاريّة", en: "Business Settings", parent: "/admin" },
  "/admin/launch-checklist": { ar: "قائمة الإطلاق", en: "Launch Checklist", parent: "/admin" },
  "/admin/payment-method-debug": { ar: "فحص وسيلة الدفع", en: "Payment Method Debug", parent: "/admin" },
  "/admin/shopify-audit": { ar: "فحص Shopify", en: "Shopify Audit", parent: "/admin" },
  "/admin/project-status": { ar: "سجلّ حالة المشروع", en: "Project Status", parent: "/admin" },
  "/admin/woo-import": { ar: "استيراد من WooCommerce", en: "WooCommerce Import", parent: "/admin" },
  "/admin/order-queue": { ar: "طابور معالجة الطلبات", en: "Order Queue", parent: "/admin/orders" },
  "/admin/order-webhooks": { ar: "طلبات ومراحل Webhooks", en: "Order Webhooks", parent: "/admin/orders" },
  "/admin/webhooks-status": { ar: "حالة Webhooks", en: "Webhooks Status", parent: "/admin" },
  "/admin/redirect-check": { ar: "الروابط القديمة", en: "Legacy URLs", parent: "/admin" },
  "/admin/ads-conversions": { ar: "تحويلات Google Ads", en: "Ads Conversions", parent: "/admin" },
  "/admin/search-insights": { ar: "تحليل كلمات البحث", en: "Search Insights", parent: "/admin" },

  "/admin/site-health": { ar: "صحّة الموقع", en: "Site Health", parent: "/admin" },
  "/admin/tracking-consistency": { ar: "اتساق التتبّع", en: "Tracking Consistency", parent: "/admin/control-center" },
  "/admin/seo-audit": { ar: "فحص SEO", en: "SEO Audit", parent: "/admin" },
  "/admin/scent-taxonomy": { ar: "تصنيفات العطور", en: "Scent Taxonomy", parent: "/admin" },
  "/admin/links": { ar: "الروابط القصيرة", en: "Short Links", parent: "/admin" },
  "/admin/links-cleanup": { ar: "تنظيف الروابط اليتيمة", en: "Orphan Links Cleanup", parent: "/admin/links" },
  "/admin/ambassador-code-audit": { ar: "سجلّ تغيير أكواد السفراء", en: "Ambassador Code Audit", parent: "/admin/ambassadors" },
  "/admin/ga4-connections": { ar: "ربوط GA4 للسفراء", en: "GA4 Connections", parent: "/admin" },
  "/admin/guide": { ar: "دليل الموقع", en: "Site Guide", parent: "/admin" },
  "/admin/terms-guide": { ar: "دليل المصطلحات", en: "Terms Guide", parent: "/admin" },
};

/**
 * الخريطة النهائيّة (المدخلات اليدويّة).
 * كود الأدمن يضيف إليها المدخلات المولَّدة عند تحميله عبر
 * `fillBreadcrumbs` في breadcrumbsMap.admin.ts — بلا استبدال ما هنا.
 */
export const BREADCRUMBS: Record<string, Crumb> = { ...MANUAL };

/** يضيف مدخلات ناقصة فقط (لا يدوس المدخلات اليدويّة). */
export function fillBreadcrumbs(extra: Record<string, Crumb>) {
  for (const [path, crumb] of Object.entries(extra)) {
    if (!BREADCRUMBS[path]) BREADCRUMBS[path] = crumb;
  }
}


/** humanize كاحتياط لأيّ مسار غير مذكور */
export function humanize(segment: string): string {
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

